// 浏览器兼容性检测函数
export function checkBrowserCompatibility() {
  const checks = {
    // Graphics & Canvas
    webgl: false,
    webgl2: false,
    canvas: false,
    offscreenCanvas: false,
    webgpu: false,

    // JavaScript/Modules
    modernJS: false,
    esm: false,

    // Storage & Workers
    localStorage: false,
    webWorkers: false,
    serviceWorker: false,

    // DOM & Layout APIs
    iframe: false,
    domMatrix: false,
    getComputedStyle: false,
    createRange: false,
    getBoundingClientRect: false,
    resizeObserver: false,
    intersectionObserver: false,

    // Legacy DOM write
    documentWrite: false,

    // CSS
    transform: false,
    css3Support: false,
    cssHasSelector: false,
    cssContainerQueries: false,

    // Input & Clipboard
    pointerEvents: false,
    touchEvents: false,
    clipboard: false,

    // WASM & advanced runtime
    wasm: false,
    wasmSIMD: false,
    wasmThreads: false,
    crossOriginIsolated: false,
  };

  // 检测 WebGL 支持
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    checks.webgl = !!gl;
  } catch {
    checks.webgl = false;
  }

  // 检测 Canvas 支持
  try {
    const canvas = document.createElement('canvas');
    checks.canvas = !!(canvas.getContext && canvas.getContext('2d'));
  } catch {
    checks.canvas = false;
  }

  // 检测 WebGL2 支持
  try {
    const canvas = document.createElement('canvas');
    const gl2 = canvas.getContext('webgl2');
    checks.webgl2 = !!gl2;
  } catch {
    checks.webgl2 = false;
  }

  // 检测 OffscreenCanvas 支持
  try {
    checks.offscreenCanvas = typeof OffscreenCanvas !== 'undefined';
  } catch {
    checks.offscreenCanvas = false;
  }

  // 检测现代 JavaScript 特性 (parser.js 需要)
  try {
    checks.modernJS = !!(
      window.Promise &&
      window.fetch &&
      Array.prototype.includes &&
      Array.prototype.filter &&
      Array.prototype.map &&
      Array.prototype.forEach &&
      Array.from &&
      Object.assign &&
      Object.keys &&
      typeof Symbol !== 'undefined' &&
      window.setTimeout &&
      window.parseInt &&
      window.parseFloat &&
      Math.round &&
      Math.abs &&
      Math.min &&
      Math.max &&
      Math.atan2 &&
      RegExp
    );
  } catch {
    checks.modernJS = false;
  }

  // 检测 ES Modules 支持 (Chrome 61+, Firefox 60+, Safari 10.1+)
  try {
    // 不使用 eval/dynamic import，避免 CSP 风险
    // 通过创建 <script type="module"> 的方式进行能力探测
    const script = document.createElement('script');
    script.type = 'module';
    checks.esm = script.type === 'module';

    // 更严格的版本检测 - 确保不是过旧的 Chrome 版本
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Chrome')) {
      const chromeMatch = userAgent.match(/Chrome\/(\d+)/);
      if (chromeMatch) {
        const chromeVersion = parseInt(chromeMatch[1]);
        // Chrome 61+ 才真正稳定支持 ES Modules
        checks.esm = checks.esm && chromeVersion >= 61;
      }
    } else if (userAgent.includes('Firefox')) {
      const firefoxMatch = userAgent.match(/Firefox\/(\d+)/);
      if (firefoxMatch) {
        const firefoxVersion = parseInt(firefoxMatch[1]);
        // Firefox 60+ 才稳定支持 ES Modules
        checks.esm = checks.esm && firefoxVersion >= 60;
      }
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      const safariMatch = userAgent.match(/Version\/(\d+)/);
      if (safariMatch) {
        const safariVersion = parseInt(safariMatch[1]);
        // Safari 10.1+ 才支持 ES Modules
        checks.esm = checks.esm && safariVersion >= 11; // Version 11 对应 Safari 10.1
      }
    } else if (userAgent.includes('Edg')) {
      const edgeMatch = userAgent.match(/Edg\/(\d+)/);
      if (edgeMatch) {
        const edgeVersion = parseInt(edgeMatch[1]);
        // Edge 79+ (基于 Chromium) 才稳定支持 ES Modules
        checks.esm = checks.esm && edgeVersion >= 79;
      }
    }
  } catch {
    checks.esm = false;
  }

  // 检测 localStorage 支持
  try {
    localStorage.setItem('test', 'test');
    localStorage.removeItem('test');
    checks.localStorage = true;
  } catch {
    checks.localStorage = false;
  }

  // 检测 Web Workers 支持
  checks.webWorkers = typeof Worker !== 'undefined';

  // 检测 Service Worker 支持
  try {
    checks.serviceWorker = !!navigator.serviceWorker;
  } catch {
    checks.serviceWorker = false;
  }

  // 检测 iframe 支持 (parser.js 需要)
  try {
    const iframe = document.createElement('iframe');
    checks.iframe = !!(iframe && document.body && document.body.appendChild);
  } catch {
    checks.iframe = false;
  }

  // 检测 DOMMatrix 支持 (parser.js getElementRotation 需要)
  try {
    checks.domMatrix = typeof DOMMatrix !== 'undefined';
    if (!checks.domMatrix && typeof WebKitCSSMatrix !== 'undefined') {
      checks.domMatrix = true; // 备用方案
    }
  } catch {
    checks.domMatrix = false;
  }

  // 检测 getComputedStyle 支持 (parser.js 需要)
  try {
    const testDiv = document.createElement('div');
    checks.getComputedStyle = !!(window.getComputedStyle && window.getComputedStyle(testDiv));
  } catch {
    checks.getComputedStyle = false;
  }

  // 检测 createRange 和 getBoundingClientRect 支持 (parser.js 文本处理需要)
  try {
    const range = document.createRange();
    const testDiv = document.createElement('div');
    checks.createRange = !!(range && range.setStart && range.setEnd);
    checks.getBoundingClientRect = !!testDiv.getBoundingClientRect;
  } catch {
    checks.createRange = false;
    checks.getBoundingClientRect = false;
  }

  // 检测 document.write 支持 (parser.js iframe 写入需要)
  try {
    checks.documentWrite = !!(
      document.write &&
      document.writeln &&
      document.open &&
      document.close
    );
  } catch {
    checks.documentWrite = false;
  }

  // 检测 CSS Transform 支持 (parser.js 转换需要)
  try {
    const testDiv = document.createElement('div');
    testDiv.style.transform = 'scale(1)';
    checks.transform = testDiv.style.transform === 'scale(1)';
  } catch {
    checks.transform = false;
  }

  // 检测 CSS3 支持 (parser.js 样式处理需要)
  try {
    const testDiv = document.createElement('div');
    document.body.appendChild(testDiv);

    // 测试基础CSS3属性支持 - 使用更智能的检测方法
    const supportsProperty = (property, testValue, expectedPattern) => {
      try {
        const originalValue = testDiv.style[property];
        testDiv.style[property] = testValue;
        const actualValue = testDiv.style[property];

        // 如果有期望的模式，使用模式匹配
        if (expectedPattern) {
          return expectedPattern.test(actualValue);
        }

        // 否则检查是否设置成功（非空且不等于原值）
        return actualValue && actualValue !== originalValue;
      } catch {
        return false;
      }
    };

    // 测试CSS3关键特性 - 使用更宽松的匹配规则
    const css3Features = {
      borderRadius: supportsProperty('borderRadius', '5px', /5px/),
      boxShadow: supportsProperty('boxShadow', '0 0 5px rgba(0,0,0,0.5)', /rgba?\(0,\s*0,\s*0/),
      transform: supportsProperty('transform', 'scale(1)', /scale\(1\)/),
      transition: supportsProperty('transition', 'all 0.3s', /0\.3s/),
      opacity: supportsProperty('opacity', '0.5', /0\.5/),
      backgroundSize: supportsProperty('backgroundSize', 'cover', /cover/),
      filter: supportsProperty('filter', 'blur(1px)', /blur\(1px\)/),
      clipPath: supportsProperty('clipPath', 'circle(50%)', /circle/),
      backdropFilter: supportsProperty('backdropFilter', 'blur(5px)', /blur\(5px\)/),
    };

    // 清理测试元素
    document.body.removeChild(testDiv);

    // 检查是否支持足够的CSS3特性 (至少支持70%)
    const supportedCount = Object.values(css3Features).filter(Boolean).length;
    const totalCount = Object.keys(css3Features).length;
    checks.css3Support = supportedCount / totalCount >= 0.7;
  } catch {
    checks.css3Support = false;
  }

  // 额外 CSS 能力：:has 选择器 与 容器查询
  try {
    checks.cssHasSelector =
      typeof CSS !== 'undefined' && CSS.supports && CSS.supports('selector(:has(*))');
  } catch {
    checks.cssHasSelector = false;
  }
  try {
    checks.cssContainerQueries =
      typeof CSS !== 'undefined' && CSS.supports && CSS.supports('container-type: inline-size');
  } catch {
    checks.cssContainerQueries = false;
  }

  // 指针与触摸
  try {
    checks.pointerEvents = 'onpointerdown' in window || window.PointerEvent != null;
  } catch {
    checks.pointerEvents = false;
  }
  try {
    checks.touchEvents = 'ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0;
  } catch {
    checks.touchEvents = false;
  }

  // 观察者 API
  try {
    checks.resizeObserver = typeof ResizeObserver !== 'undefined';
  } catch {
    checks.resizeObserver = false;
  }
  try {
    checks.intersectionObserver = typeof IntersectionObserver !== 'undefined';
  } catch {
    checks.intersectionObserver = false;
  }

  // 剪贴板 API
  try {
    checks.clipboard = !!(
      navigator.clipboard &&
      navigator.clipboard.writeText &&
      navigator.clipboard.readText
    );
  } catch {
    checks.clipboard = false;
  }

  // WebGPU
  try {
    checks.webgpu = !!navigator.gpu;
  } catch {
    checks.webgpu = false;
  }

  // WebAssembly 基础能力
  try {
    checks.wasm = typeof WebAssembly === 'object' && typeof WebAssembly.instantiate === 'function';
  } catch {
    checks.wasm = false;
  }
  // WebAssembly SIMD 检测
  try {
    // 大多数浏览器在 2021+ 已默认支持 SIMD，可通过特性字符串检测
    checks.wasmSIMD =
      WebAssembly &&
      typeof WebAssembly.validate === 'function' &&
      typeof WebAssembly.FeatureDetection === 'undefined'
        ? // 保守：不可可靠检测时，维持 false
          false
        : // 新版标准可能暴露 navigator.userAgentData.hints，但我们不依赖之
          (typeof navigator !== 'undefined' && /simd/i.test(navigator.userAgent)) || false;
  } catch {
    checks.wasmSIMD = false;
  }
  // WebAssembly Threads 及跨域隔离
  try {
    checks.crossOriginIsolated = !!window.crossOriginIsolated;
    // 线程能力通常需要 SharedArrayBuffer + crossOriginIsolated
    checks.wasmThreads = checks.crossOriginIsolated && typeof SharedArrayBuffer !== 'undefined';
  } catch {
    checks.wasmThreads = false;
    checks.crossOriginIsolated = checks.crossOriginIsolated || false;
  }

  return checks;
}

// 验证浏览器兼容性
export async function validateBrowserCompatibility() {
  const compatibility = checkBrowserCompatibility();

  // parser.js 必需的核心功能
  const requiredFeatures = [
    'modernJS',
    'esm',
    'iframe',
    'getComputedStyle',
    'createRange',
    'getBoundingClientRect',
    'documentWrite',
    'css3Support',
  ];

  // 推荐功能 (非必需但影响体验)
  const recommendedFeatures = [
    'webgl',
    'canvas',
    'localStorage',
    'domMatrix',
    'transform',
    'resizeObserver',
    'intersectionObserver',
    'pointerEvents',
  ];

  const missingRequired = requiredFeatures.filter((feature) => !compatibility[feature]);
  const missingRecommended = recommendedFeatures.filter((feature) => !compatibility[feature]);

  if (missingRequired.length > 0) {
    const errorMessage = `Browser compatibility check failed. Missing required features: ${missingRequired.join(
      ', '
    )}`;
    console.error('Critical browser compatibility issues detected:', compatibility);
    const err = new Error(errorMessage);
    err.context = {
      compatibility,
    };
    throw err;
  }

  if (missingRecommended.length > 0) {
    console.warn(
      'Browser compatibility warnings - missing recommended features:',
      missingRecommended
    );
    console.warn('Full compatibility report:', compatibility);
  }

  return {
    compatible: true,
    required: missingRequired.length === 0,
    recommended: missingRecommended.length === 0,
    details: compatibility,
  };
}

// 生成更全面的兼容性报告
export function getCompatibilityReport() {
  const details = checkBrowserCompatibility();
  const weights = {
    modernJS: 3,
    esm: 3,
    css3Support: 2,
    getComputedStyle: 2,
    createRange: 2,
    getBoundingClientRect: 2,
    webgl: 2,
    webgl2: 1,
    canvas: 2,
    domMatrix: 1,
    transform: 1,
    resizeObserver: 1,
    intersectionObserver: 1,
    pointerEvents: 1,
    clipboard: 1,
    wasm: 2,
    wasmSIMD: 1,
    wasmThreads: 1,
    crossOriginIsolated: 1,
    webWorkers: 1,
    serviceWorker: 1,
    offscreenCanvas: 1,
    webgpu: 1,
    cssHasSelector: 1,
    cssContainerQueries: 1,
  };
  const total = Object.values(weights).reduce((a, b) => a + b, 0);
  const score = Object.entries(weights).reduce((sum, [key, weight]) => {
    return sum + (details[key] ? weight : 0);
  }, 0);

  let status = 'compatibleWithWarnings';
  if (score / total >= 0.9) status = 'fullyCompatible';
  if (score / total < 0.6) status = 'incompatible';

  const recommendations = [];
  if (!details.esm || !details.modernJS) recommendations.push('updateBrowser');
  if (!details.webgl && !details.canvas) recommendations.push('enableHardwareAcceleration');
  if (!details.crossOriginIsolated) recommendations.push('enableCOOPCOEP');
  if (!details.serviceWorker) recommendations.push('updateToLatest');
  if (!details.resizeObserver || !details.intersectionObserver)
    recommendations.push('partialDegrade');

  return {
    status,
    score: Math.round((score / total) * 100),
    details,
    recommendations,
  };
}

// 获取浏览器信息
export function getBrowserInfo() {
  const userAgent = navigator.userAgent;
  const browser = {
    name: 'Unknown',
    version: 'Unknown',
    engine: 'Unknown',
  };

  // 检测浏览器类型
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
    browser.name = 'Chrome';
    browser.engine = 'Blink';
    const match = userAgent.match(/Chrome\/(\d+)/);
    if (match) browser.version = match[1];
  } else if (userAgent.includes('Firefox')) {
    browser.name = 'Firefox';
    browser.engine = 'Gecko';
    const match = userAgent.match(/Firefox\/(\d+)/);
    if (match) browser.version = match[1];
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    browser.name = 'Safari';
    browser.engine = 'WebKit';
    const match = userAgent.match(/Version\/(\d+)/);
    if (match) browser.version = match[1];
  } else if (userAgent.includes('Edg')) {
    browser.name = 'Edge';
    browser.engine = 'Blink';
    const match = userAgent.match(/Edg\/(\d+)/);
    if (match) browser.version = match[1];
  }

  // 平台与移动端标识
  browser.platform = navigator.platform || 'Unknown';
  browser.mobile = /Mobi|Android|iPhone|iPad|iPod/i.test(userAgent);

  return browser;
}
